import { useState, type FormEvent } from "react";
import { VALIDADES_TOKEN_PESSOAL, type TokenPessoalDto } from "@base/contracts";
import { CampoDeTexto, EstadoVazio, MolduraDoApp } from "@base/ui";
import { Button, Modal } from "@base/ui/renke";
import { Bot, Copy } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageLayout } from "@/components/layout/page-header";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { useDialogos } from "../../../shared/ui/dialogos.contexto";
import { useNavegacaoDoApp } from "../../referencias/presentation/navegacao-do-app";
import { rodapeDaSessaoNaLateral } from "../../referencias/presentation/rodape-da-sessao";
import {
  useCriarTokenPessoal,
  useRevogarTokenPessoal,
  useTokensPessoais,
} from "../application/use-tokens-pessoais";
import {
  recadoDepoisDeConectar,
  trechosDeConfiguracao,
  urlDoServidorMcp,
  type ClienteMcp,
} from "../domain/trechos-de-configuracao";

const EXEMPLOS = [
  "Agende o vídeo X para sexta 18h no Instagram e no TikTok",
  "O que está na fila esta semana?",
  "Por que o post de ontem falhou? Reprocessa",
  "Acrescente no fim de cada post que ainda não saiu: Digite eu quero para receber o nosso ebook",
  "Passe o post de amanhã no Instagram para sexta, às 19h",
  "Troque a mídia do post de quinta pela foto nova da biblioteca",
  "Quais redes estão conectadas?",
] as const;

type ValidadeForm = `${(typeof VALIDADES_TOKEN_PESSOAL)[number]}` | "nunca";

function mensagemDeErro(causa: unknown): string {
  if (causa instanceof ErroDaApi) return causa.message;
  if (causa instanceof Error) return causa.message;
  return "Não deu para concluir. Tente de novo.";
}

function formatarQuando(iso: string | null): string {
  if (!iso) return "Não expira";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(iso));
}

function formatarUso(iso: string | null): string {
  if (!iso) return "Ainda não";
  return formatarQuando(iso);
}

export function PaginaMcp() {
  const seletor = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("mcp");
  const lista = useTokensPessoais();
  const criar = useCriarTokenPessoal();
  const revogar = useRevogarTokenPessoal();
  const { confirmar } = useDialogos();

  const [modalAberto, definirModalAberto] = useState(false);
  const [nome, definirNome] = useState("");
  const [validade, definirValidade] = useState<ValidadeForm>("90");
  const [erroForm, definirErroForm] = useState<string | null>(null);
  const [tokenRevelado, definirTokenRevelado] = useState<string | null>(null);
  const [cliente, definirCliente] = useState<ClienteMcp>("claude-code");
  const [copiado, definirCopiado] = useState<string | null>(null);

  const ativos = (lista.data?.itens ?? []).filter((item) => !item.revogadoEm);
  const url =
    typeof window === "undefined"
      ? "/api/mcp"
      : urlDoServidorMcp(window.location.origin);
  const trechos = trechosDeConfiguracao(url, tokenRevelado);
  const recado = recadoDepoisDeConectar();

  function limparFormulario() {
    definirNome("");
    definirValidade("90");
    definirErroForm(null);
  }

  function fecharModal() {
    if (criar.isPending) return;
    definirModalAberto(false);
    limparFormulario();
  }

  function aoGerar(evento: FormEvent) {
    evento.preventDefault();
    definirErroForm(null);
    const nomeLimpo = nome.trim();
    if (nomeLimpo.length < 2) {
      definirErroForm("Dê um nome com pelo menos 2 caracteres.");
      return;
    }
    criar.mutate(
      {
        nome: nomeLimpo,
        validadeDias: validade === "nunca" ? null : Number(validade),
      },
      {
        onSuccess: (resposta) => {
          definirTokenRevelado(resposta.token);
          definirModalAberto(false);
          limparFormulario();
        },
        onError: (causa) => definirErroForm(mensagemDeErro(causa)),
      },
    );
  }

  async function aoRevogar(item: TokenPessoalDto) {
    const ok = await confirmar({
      titulo: "Revogar este token?",
      descricao: `O ${item.nome} para de funcionar na próxima chamada. Depois dá para gerar outro.`,
      rotuloConfirmar: "Revogar",
    });
    if (!ok) return;
    revogar.mutate(item.id);
  }

  async function copiar(texto: string, chave: string) {
    await navigator.clipboard.writeText(texto);
    definirCopiado(chave);
  }

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Conectar a IA"
      subtitulo="Ligue o Creative OS no ChatGPT ou no Claude. Aí o chat pergunta o projeto e espera o seu sim."
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletor}
      acoes={
        <Button
          type="button"
          variante="brand"
          tamanho="md"
          onClick={() => definirModalAberto(true)}
        >
          <Bot className="size-4" aria-hidden />
          Gerar token
        </Button>
      }
    >
      <PageLayout className="animate-aparecer space-y-8">
        <section className="max-w-3xl space-y-6">
          <div className="space-y-2">
            <p className="text-base leading-relaxed text-foreground">
              Colar o endereço numa mensagem não liga a ferramenta. O ChatGPT
              precisa do Creative OS na lista de apps.
            </p>
          </div>
          <ol className="grid gap-4 sm:grid-cols-3">
            <li className="space-y-2 rounded-xl border border-border-subtle px-4 py-4">
              <p className="text-sm font-semibold text-foreground">1. Crie o app</p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                No ChatGPT: Configurações, Apps, modo de desenvolvedor, criar
                app. Nome: Creative OS. Autenticação: OAuth. Endereço é o de baixo.
              </p>
            </li>
            <li className="space-y-2 rounded-xl border border-border-subtle px-4 py-4">
              <p className="text-sm font-semibold text-foreground">2. Permita o acesso</p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                O ChatGPT abre esta conta. Entre e toque em Permitir. No Claude
                a tela é Conectores, com o mesmo endereço.
              </p>
            </li>
            <li className="space-y-2 rounded-xl border border-border-subtle px-4 py-4">
              <p className="text-sm font-semibold text-foreground">3. Fale no chat novo</p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Ative o Creative OS nas ferramentas da conversa. Ele pergunta o
                projeto, tipo Renke Academy, e espera o seu sim antes de gravar.
              </p>
            </li>
          </ol>
        </section>

        {tokenRevelado ? (
          <Alert>
            <AlertTitle>Copie agora</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                Este valor não aparece de novo. Se perder, revogue e gere
                outro.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <code className="min-w-0 flex-1 break-all rounded-lg bg-muted px-3 py-2 text-sm">
                  {tokenRevelado}
                </code>
                <Button
                  type="button"
                  variante="secondary"
                  tamanho="sm"
                  onClick={() => void copiar(tokenRevelado, "token")}
                >
                  <Copy className="size-4" aria-hidden />
                  {copiado === "token" ? "Copiado" : "Copiar"}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        <Card variant="solid">
          <CardHeader className="space-y-2">
            <CardTitle>Endereço do app</CardTitle>
            <CardDescription>
              Cole este endereço na criação do app. A autenticação é OAuth.
              Não cole no meio da conversa.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <code className="min-w-0 flex-1 break-all rounded-lg bg-muted px-3 py-2 text-sm">
                {url}
              </code>
              <Button
                type="button"
                variante="secondary"
                tamanho="sm"
                onClick={() => void copiar(url, "url")}
              >
                <Copy className="size-4" aria-hidden />
                {copiado === "url" ? "Copiado" : "Copiar endereço"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card variant="solid">
          <CardHeader className="space-y-2">
            <CardTitle>O que falar depois de ligar</CardTitle>
            <CardDescription>
              Só use isto quando o Creative OS já aparecer nas ferramentas do
              chat. Sem chave no texto.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-muted p-4 text-sm leading-relaxed">
              {recado}
            </pre>
            <Button
              type="button"
              variante="secondary"
              tamanho="sm"
              onClick={() => void copiar(recado, "mensagem")}
            >
              <Copy className="size-4" aria-hidden />
              {copiado === "mensagem" ? "Copiado" : "Copiar recado"}
            </Button>
          </CardContent>
        </Card>

        <Card variant="solid">
          <CardHeader className="space-y-2">
            <CardTitle>Suas chaves</CardTitle>
            <CardDescription>
              Chave para o Claude no computador e o Cursor. O ChatGPT no site
              não usa isto: ele pede permissão na tela.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lista.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : null}
            {lista.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Não carregou os tokens</AlertTitle>
                <AlertDescription>
                  {mensagemDeErro(lista.error)}
                </AlertDescription>
              </Alert>
            ) : null}
            {!lista.isLoading && !lista.isError && ativos.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum token ainda"
                descricao="O ChatGPT não precisa desta chave. Ela é para o Claude no computador e o Cursor."
              />
            ) : null}
            {ativos.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Início</TableHead>
                    <TableHead>Criado</TableHead>
                    <TableHead>Último uso</TableHead>
                    <TableHead>Validade</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ativos.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.nome}</TableCell>
                      <TableCell>
                        <code>{item.prefixo}</code>
                      </TableCell>
                      <TableCell>{formatarQuando(item.criadoEm)}</TableCell>
                      <TableCell>{formatarUso(item.ultimoUsoEm)}</TableCell>
                      <TableCell>
                        {item.expiraEm
                          ? formatarQuando(item.expiraEm)
                          : "Não expira"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variante="ghost"
                          tamanho="sm"
                          disabled={revogar.isPending}
                          onClick={() => void aoRevogar(item)}
                        >
                          Revogar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </CardContent>
        </Card>

        <Card variant="solid">
          <CardHeader className="space-y-2">
            <CardTitle>Se o app pedir um arquivo</CardTitle>
            <CardDescription>
              Claude no computador ou Cursor. Gere a chave antes: ela entra no
              texto. Quem só usa o chat do ChatGPT pode pular.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs
              value={cliente}
              onValueChange={(valor) => definirCliente(valor as ClienteMcp)}
            >
              <TabsList>
                {trechos.map((trecho) => (
                  <TabsTrigger key={trecho.cliente} value={trecho.cliente}>
                    {trecho.rotulo}
                  </TabsTrigger>
                ))}
              </TabsList>
              {trechos.map((trecho) => (
                <TabsContent
                  key={trecho.cliente}
                  value={trecho.cliente}
                  className="space-y-3 pt-2"
                >
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {trecho.onde}
                  </p>
                  <pre className="overflow-x-auto rounded-lg bg-muted p-4 text-sm leading-relaxed">
                    <code>{trecho.codigo}</code>
                  </pre>
                  <Button
                    type="button"
                    variante="secondary"
                    tamanho="sm"
                    onClick={() => void copiar(trecho.codigo, trecho.cliente)}
                  >
                    <Copy className="size-4" aria-hidden />
                    {copiado === trecho.cliente ? "Copiado" : "Copiar trecho"}
                  </Button>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        <Card variant="solid">
          <CardHeader className="space-y-2">
            <CardTitle>Exemplos de pedido</CardTitle>
            <CardDescription>
              Copie um e mande no chat, dizendo o projeto. A IA confirma antes
              de mudar qualquer coisa.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-3">
              {EXEMPLOS.map((exemplo) => (
                <li
                  key={exemplo}
                  className="rounded-lg border border-border-subtle px-4 py-3 text-sm leading-relaxed"
                >
                  {exemplo}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </PageLayout>

      <Modal
        aberto={modalAberto}
        titulo="Gerar token"
        descricao="Dê um nome que você reconheça depois, tipo Claude no celular."
        onFechar={fecharModal}
        mostrarFechar={false}
        rodape={
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button
              type="button"
              variante="secondary"
              tamanho="sm"
              onClick={fecharModal}
              disabled={criar.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="form-token-pessoal"
              variante="primary"
              tamanho="sm"
              carregando={criar.isPending}
              disabled={criar.isPending || nome.trim().length < 2}
            >
              Gerar
            </Button>
          </div>
        }
      >
        <form
          id="form-token-pessoal"
          className="mt-4 space-y-4"
          onSubmit={aoGerar}
        >
          <CampoDeTexto
            rotulo="Nome"
            value={nome}
            onChange={(evento) => definirNome(evento.target.value)}
            placeholder="Claude no notebook"
            required
            autoFocus
          />
          <div className="grid gap-2">
            <Label htmlFor="validade-token">Validade</Label>
            <Select
              value={validade}
              onValueChange={(valor) => definirValidade(valor as ValidadeForm)}
            >
              <SelectTrigger id="validade-token">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
                <SelectItem value="365">1 ano</SelectItem>
                <SelectItem value="nunca">Sem expirar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {erroForm ? (
            <p className="text-sm text-destructive" role="alert">
              {erroForm}
            </p>
          ) : null}
        </form>
      </Modal>
    </MolduraDoApp>
  );
}
